/** Workspace settings, profile, branding, danger zone (pages/settings.tsx, components/settings/*). */
export const settings = {
  page: {
    title: "Settings",
    description: "Manage workspace preferences and configuration.",
  },

  error: {
    title: "Couldn't load settings",
    description:
      "Something went wrong while loading your workspace settings. Check your connection and try again.",
  },

  // ASCII-dot busy variants (distinct from common's ellipsis "Saving…").
  status: {
    savingDots: "Saving...",
    uploadingDots: "Uploading...",
    deletingDots: "Deleting...",
  },

  toast: {
    saved: "Settings saved successfully",
    saveFailed: "Failed to save settings",
    deleted: "Workspace deleted",
    deleteFailed: "Failed to delete workspace",
  },

  profile: {
    title: "Workspace Profile",
    description: "Update your workspace's basic information.",
    name: { label: "Workspace Name" },
    timezone: {
      label: "Default timezone",
      help: "Used as the default for new locations and schedules.",
    },
    button: { save: "Save Changes" },
  },

  branding: {
    title: "Branding",
    description:
      "Optional company name, accent color, logo, and icon. Leave blank to use OmniMedia defaults.",
    companyName: {
      label: "Company name",
      placeholder: "Display name",
    },
    accentColor: {
      label: "Accent color",
      placeholder: "250 85% 50% or #3B5BDB",
      pickAriaLabel: "Pick accent color",
      invalid: "Use an HSL triplet such as 250 85% 50%, or hex such as #3B5BDB.",
      preview: "Buttons look like this",
    },
    asset: {
      logo: { label: "Logo", description: "Wide image for headers" },
      icon: { label: "Icon", description: "Square image for lists" },
    },
    button: { save: "Save branding" },
    toast: {
      saved: "Branding saved",
      saveFailed: "Failed to save branding",
      invalidType: "Use PNG, JPG, WebP, or SVG",
      logoTooLarge: "Logo must be under 2 MB",
      iconTooLarge: "Icon must be under 512 KB",
      logoUploaded: "Logo uploaded",
      iconUploaded: "Icon uploaded",
      uploadFailed: "Upload failed",
      logoRemoved: "Logo removed",
      iconRemoved: "Icon removed",
      removeFailed: "Failed to remove image",
    },
  },

  content: {
    title: "Content",
    description:
      "Control what plays across every location and device in this workspace.",
    blockExplicit: {
      label: "Block explicit music",
      description:
        "Skip explicit catalogue tracks on all players and prevent adding them to playlists.",
    },
    toast: {
      blocked: {
        title: "Explicit music blocked",
        description:
          "Players skip explicit tracks and they can't be added to playlists.",
      },
      allowed: {
        title: "Explicit music allowed",
        description: "Explicit catalogue tracks can play again.",
      },
      saveFailed: "Failed to save content filter",
    },
  },

  // Offline-device alert card (workspace-alerts-card.tsx).
  alerts: {
    title: "Alerts",
    description:
      "Get notified when something in your fleet needs attention.",
    offline: {
      label: "Email me when a device goes offline",
      description:
        "One email per outage to the workspace owner, plus a note when the device comes back.",
      thresholdLabel: "Alert after",
      threshold: {
        min5: "5 minutes offline",
        min10: "10 minutes offline",
        min15: "15 minutes offline",
        min30: "30 minutes offline",
        hour1: "1 hour offline",
        hours2: "2 hours offline",
        hours4: "4 hours offline",
      },
    },
    toast: {
      enabled: {
        title: "Offline alerts on",
        description:
          "The workspace owner will be emailed when a device stays offline.",
      },
      disabled: {
        title: "Offline alerts off",
      },
      thresholdSaved: "Alert timing saved",
      saveFailed: "Failed to save alert settings",
    },
  },

  danger: {
    title: "Danger zone",
    description:
      "Permanently delete this workspace and all of its devices, media, playlists, schedules, and member access. This cannot be undone.",
    deleteButton: "Delete workspace",
    dialog: {
      title: "Delete workspace?",
      // Split around the bolded, interpolated workspace name (no markdown renderer here).
      descPrefix: "This will permanently delete",
      descSuffix: "and everything in it. Type the workspace name to confirm.",
    },
  },

  // Machine API keys (service accounts).
  apiKeys: {
    title: "API keys",
    description:
      "Machine keys for integrations and scripts. Each key acts with its own scoped permissions and can be revoked at any time.",
    empty: "No API keys yet.",
    create: "New API key",
    createTitle: "Create an API key",
    nameLabel: "Name",
    namePlaceholder: "e.g. Signage sync script",
    expiresLabel: "Expires (optional)",
    mint: "Create key",
    mintedTitle: "Copy your API key",
    mintedOnce:
      "This is the only time the full key is shown. Store it somewhere safe. You can't retrieve it again.",
    revoke: "Revoke",
    revokeConfirm: 'Revoke "{name}"? Anything using it stops working immediately.',
    lastUsed: " · last used {date}",
    neverUsed: " · never used",
    expires: " · expires {date}",
    status: {
      active: "Active",
      expired: "Expired",
      revoked: "Revoked",
    },
    toast: {
      createFailed: "Couldn't create the key",
      revoked: "API key revoked",
      revokeFailed: "Couldn't revoke the key",
    },
  },
} as const;
